/** Shared primitives: OutlierOS look (radius 24/16/12, compact buttons, 10 px uppercase labels). */
import { useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { AlertTriangle, Loader2, RotateCw, X } from 'lucide-react';
import { cn } from './cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline-danger';
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-primary text-primary-foreground hover:bg-primary-hover shadow-sm',
  secondary: 'border border-border bg-card text-foreground hover:bg-secondary shadow-xs',
  ghost: 'text-muted-foreground hover:bg-secondary hover:text-foreground',
  danger: 'bg-rose-600 text-white hover:bg-rose-700',
  'outline-danger': 'border border-rose-500/40 text-rose-600 hover:bg-rose-500/10 dark:text-rose-400',
};

export function Button({ variant = 'primary', size = 'md', loading, className, children, disabled, ...rest }:
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' | 'lg'; loading?: boolean }) {
  const sizes = { sm: 'px-2.5 py-1.5 text-xs', md: 'px-3.5 py-2 text-xs', lg: 'px-5 py-2.5 text-sm' };
  return (
    <button {...rest} disabled={disabled || loading}
      className={cn('inline-flex items-center justify-center gap-1.5 rounded-xl font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary', sizes[size], VARIANTS[variant], className)}>
      {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {children}
    </button>
  );
}

export function IconButton({ label, className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button {...rest} aria-label={label} title={label}
      className={cn('inline-flex items-center justify-center rounded-xl border border-border bg-card p-2 text-foreground shadow-sm transition-colors hover:bg-secondary disabled:opacity-50', className)}>
      {children}
    </button>
  );
}

export function Card({ className, children, as: As = 'div' }: { className?: string; children: ReactNode; as?: 'div' | 'section' }) {
  return <As className={cn('rounded-2xl border border-border bg-card shadow-xs', className)}>{children}</As>;
}

/** Page header card (OutlierOS rounded-3xl p-6 sm:p-8 header). */
export function HeaderCard({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={cn('space-y-6 rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-8', className)}>{children}</section>;
}

export function Pill({ children, className, tone = 'primary' }: { children: ReactNode; className?: string; tone?: 'primary' | 'amber' | 'emerald' | 'rose' | 'muted' | 'sky' | 'violet' }) {
  const tones = {
    primary: 'bg-primary/10 text-primary border-primary/20',
    amber: 'bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400',
    emerald: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400',
    rose: 'bg-rose-500/10 text-rose-600 border-rose-500/20 dark:text-rose-400',
    muted: 'bg-secondary text-muted-foreground border-border',
    sky: 'bg-sky-500/10 text-sky-600 border-sky-500/20 dark:text-sky-400',
    violet: 'bg-violet-500/10 text-violet-600 border-violet-500/20 dark:text-violet-400',
  };
  return <span className={cn('inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-bold', tones[tone], className)}>{children}</span>;
}

/** Stat chip in header cards: "{n} Creators". */
export function StatChip({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div className="rounded-xl border border-border bg-secondary/60 px-3 py-2 text-center">
      <div className="text-sm font-extrabold tabular-nums">{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

export function Segmented<T extends string>({ value, onChange, options, className, size = 'md' }:
  { value: T; onChange: (v: T) => void; options: Array<{ value: T; label: ReactNode; title?: string }>; className?: string; size?: 'sm' | 'md' }) {
  return (
    <div role="tablist" className={cn('inline-flex flex-wrap rounded-xl bg-secondary p-1', className)}>
      {options.map((o) => (
        <button key={o.value} role="tab" aria-selected={value === o.value} title={o.title} onClick={() => onChange(o.value)}
          className={cn('inline-flex items-center gap-1.5 rounded-lg font-semibold transition-colors', size === 'sm' ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs',
            value === o.value ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground')}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Spinner({ label, className }: { label?: string; className?: string }) {
  return (
    <div className={cn('flex items-center justify-center gap-3 py-16 text-sm text-muted-foreground', className)} role="status">
      <Loader2 className="h-5 w-5 animate-spin text-primary" />
      {label}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-xl bg-secondary', className)} />;
}

export function EmptyState({ icon, title, children, actions }: { icon: ReactNode; title: string; children?: ReactNode; actions?: ReactNode }) {
  return (
    <section className="mx-auto flex max-w-2xl flex-col items-center gap-3 rounded-3xl border border-border bg-card px-6 py-12 text-center shadow-xs">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">{icon}</div>
      <h2 className="text-lg font-extrabold">{title}</h2>
      {children && <div className="max-w-md text-sm text-muted-foreground">{children}</div>}
      {actions && <div className="mt-2 flex flex-wrap justify-center gap-2">{actions}</div>}
    </section>
  );
}

export function ErrorState({ message, onRetry, homeHref, reference, supportEmail }: { message?: string; onRetry?: () => void; homeHref?: string; reference?: string; supportEmail?: string }) {
  return (
    <section className="mx-auto flex max-w-lg flex-col items-center gap-3 rounded-3xl border border-border bg-card px-6 py-10 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-500/10 text-rose-500"><AlertTriangle className="h-6 w-6" /></div>
      <h2 className="text-lg font-extrabold">Something went wrong</h2>
      <p className="text-sm text-muted-foreground">{message ?? 'This part of the app hit an unexpected error. Your data is safe. Try again, or go back.'}</p>
      <div className="flex gap-2">
        {onRetry && <Button onClick={onRetry}><RotateCw className="h-3.5 w-3.5" />Try again</Button>}
        {homeHref && <a href={homeHref} className="inline-flex items-center rounded-xl border border-border px-3.5 py-2 text-xs font-semibold hover:bg-secondary">Go back</a>}
      </div>
      {(reference || supportEmail) && <p className="font-mono text-[11px] text-muted-foreground">{reference ? `Reference: ${reference}` : ''}{reference && supportEmail ? ' · ' : ''}{supportEmail}</p>}
    </section>
  );
}

/**
 * Modal: overlay click and Esc close it. On open, focus moves to the first [autofocus] element (or the dialog)
 * and returns to the opener on close. `onClose` is read through a ref so parent re-renders never steal focus.
 */
export function Modal({ open, onClose, title, children, footer, size = 'md', labelledBy }:
  { open: boolean; onClose: () => void; title?: ReactNode; children: ReactNode; footer?: ReactNode; size?: 'sm' | 'md' | 'lg' | 'xl'; labelledBy?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close.current();
    window.addEventListener('keydown', onKey);
    const prev = document.activeElement as HTMLElement | null;
    const target = ref.current?.querySelector<HTMLElement>('[autofocus],[data-autofocus]') ?? ref.current;
    target?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [open]);
  if (!open) return null;
  const widths = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-2xl', xl: 'max-w-6xl' };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-fade-in" onMouseDown={(e) => e.target === e.currentTarget && close.current()}>
      <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={labelledBy}
        className={cn('w-full animate-zoom-in rounded-2xl border border-border bg-card p-6 shadow-2xl outline-none', widths[size])}>
        {title && (
          <div className="mb-4 flex items-start justify-between gap-3">
            <h2 id={labelledBy} className="text-base font-extrabold">{title}</h2>
            <button aria-label="Close" onClick={() => close.current()} className="rounded-lg p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"><X className="h-4 w-4" /></button>
          </div>
        )}
        {children}
        {footer && <div className="mt-6 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * Confirmation dialog (replaces OutlierOS window.confirm). With `typeToConfirm`, the user must type that word
 * before the destructive button enables (rule 14 for bulk deletes).
 */
export function ConfirmDialog({ open, title, body, confirmLabel = 'Confirm', danger, typeToConfirm, onConfirm, onCancel, busy }:
  { open: boolean; title: string; body: ReactNode; confirmLabel?: string; danger?: boolean; typeToConfirm?: string; onConfirm: (typed: string) => void; onCancel: () => void; busy?: boolean }) {
  const [typed, setTyped] = useState('');
  useEffect(() => setTyped(''), [open]);
  const ok = !typeToConfirm || typed.trim() === typeToConfirm;
  return (
    <Modal open={open} onClose={onCancel} title={title} labelledBy="confirm-title"
      footer={<>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button variant={danger ? 'danger' : 'primary'} disabled={!ok} loading={busy} onClick={() => onConfirm(typed)}>{confirmLabel}</Button>
      </>}>
      <div className="space-y-3 text-sm text-muted-foreground">{body}</div>
      {typeToConfirm && (
        <label className="mt-4 block text-xs font-semibold">
          Type <span className="font-mono text-foreground">{typeToConfirm}</span> to confirm
          <input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-2 font-mono text-sm outline-none focus:border-primary" />
        </label>
      )}
    </Modal>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded-md border border-border bg-secondary px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">{children}</kbd>;
}

/** Text input with an optional leading icon. */
export function Input({ icon, className, ...rest }: React.InputHTMLAttributes<HTMLInputElement> & { icon?: ReactNode }) {
  return (
    <div className={cn('relative', className)}>
      {icon && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{icon}</span>}
      <input {...rest} className={cn('w-full rounded-xl border border-border bg-background py-2 pr-3 text-sm outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary', icon ? 'pl-9' : 'pl-3')} />
    </div>
  );
}

/** Native select styled like OutlierOS sort controls. */
export function Select({ className, children, ...rest }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} className={cn('rounded-xl border border-border bg-card px-3 py-2 text-xs font-semibold outline-none focus:border-primary', className)}>
      {children}
    </select>
  );
}
